'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Moon, Sun } from 'lucide-react'

/**
 * Переключатель темы. Хранит выбор в localStorage, применяет к <html> через класс 'dark'.
 * Поддерживает 'auto' (системные настройки) — но по умолчанию используется явный выбор пользователя.
 */
export function ThemeToggle() {
  const t = useTranslations()
  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    // Класс уже выставлен синхронным скриптом в <head> (app/layout.tsx) —
    // здесь только считываем его, чтобы иконка совпала с реальной темой.
    setMounted(true)
    setTheme(document.documentElement.classList.contains('dark') ? 'dark' : 'light')
  }, [])

  function applyTheme(t: 'light' | 'dark') {
    if (t === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  function toggle() {
    const next = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    applyTheme(next)
    try { localStorage.setItem('theme', next) } catch {}
  }

  // Чтобы избежать hydration mismatch — пока не смонтировались, отдаём плейсхолдер
  if (!mounted) {
    return (
      <button
        type="button"
        className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-ink-500 hover:bg-ink-100 hover:text-ink-900 transition-colors"
        aria-label={t('shell.theme')}
      >
        <Sun size={18} />
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={toggle}
      title={theme === 'dark' ? t('shell.lightTheme') : t('shell.darkTheme')}
      className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-ink-500 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-800 hover:text-ink-900 dark:hover:text-white transition-colors"
      aria-label={theme === 'dark' ? t('shell.switchToLight') : t('shell.switchToDark')}
    >
      {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  )
}
